import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/benefit-plan-form/ require four levels up to reach convex/_generated/
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useOrgContext } from '@/hooks/useOrgContext';
import { useBenefitPlanForm } from '@/hooks/useBenefitPlanForm';
import { useBenefitPlanBasics } from '@/hooks/useBenefitPlanBasics';
import type { IExtractedBenefitPlan, IBenefitSection, IBenefitItem } from '@/@types/claims';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, Upload, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { ImportPlanDialog } from './ImportPlanDialog';
import { PlanDetailsCard } from './PlanDetailsCard';
import { SectionEditor } from './SectionEditor';

export function BenefitPlanFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const isEditing = id !== undefined && id !== 'new';
  const planId = isEditing ? (id as Id<'benefitPlans'>) : null; // AS_JUSTIFIED: one-time Id cast from route params

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const { state, setField, loadFromPlan, applyImportedConditions } = useBenefitPlanBasics();
  const sectionForm = useBenefitPlanForm();

  const existingPlan = useQuery(api.insurance.benefitPlans.get, planId ? { planId } : 'skip');
  const providers = useQuery(api.insurance.providers.list, currentOrg ? { orgId: currentOrg._id } : 'skip');
  const orgTypes = useQuery(api.insurance.types.listOrgTypes, currentOrg ? { orgId: currentOrg._id } : 'skip');

  const createPlan = useMutation(api.insurance.benefitPlans.create);
  const updatePlan = useMutation(api.insurance.benefitPlans.update);
  const createSection = useMutation(api.insurance.benefitSections.create);
  const createItem = useMutation(api.insurance.benefitItems.create);

  useEffect(() => {
    if (!existingPlan) return;
    loadFromPlan(existingPlan);
    sectionForm.loadSections(
      existingPlan.sections.map((s: IBenefitSection & { items: IBenefitItem[] }) => ({
        tempId: s._id,
        name: s.name,
        sortOrder: s.sortOrder,
        items: s.items.map((it: IBenefitItem) => ({
          tempId: it._id, name: it.name, description: it.description ?? '',
          planPaysPercent: String(it.planPaysPercent), youPayPercent: String(it.youPayPercent),
          annualLimit: it.annualLimit !== undefined ? String(it.annualLimit) : '',
          lifetimeLimit: it.lifetimeLimit !== undefined ? String(it.lifetimeLimit) : '',
          visitLimit: it.visitLimit !== undefined ? String(it.visitLimit) : '',
          waitingPeriodMonths: it.waitingPeriodMonths !== undefined ? String(it.waitingPeriodMonths) : '',
          cashBenefitPerUnit: it.cashBenefitPerUnit !== undefined ? String(it.cashBenefitPerUnit) : '',
          cashBenefitUnit: it.cashBenefitUnit ?? '',
          cashBenefitMaxUnits: it.cashBenefitMaxUnits !== undefined ? String(it.cashBenefitMaxUnits) : '',
          sortOrder: it.sortOrder,
        })),
      }))
    );
    // loadFromPlan and loadSections are stable (useCallback/useState setters), intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPlan]);

  const handleImport = (data: IExtractedBenefitPlan) => {
    applyImportedConditions(data.planConditions);
    sectionForm.loadSections(
      data.sections.map((s, si) => ({
        tempId: `import-${si}-${Math.random().toString(36).slice(2)}`,
        name: s.name, sortOrder: si,
        items: s.items.map((it, ii) => ({
          tempId: `import-${si}-${ii}-${Math.random().toString(36).slice(2)}`,
          name: it.name, description: it.description ?? '',
          planPaysPercent: it.planPaysPercent !== undefined ? String(it.planPaysPercent) : '100',
          youPayPercent: it.youPayPercent !== undefined ? String(it.youPayPercent) : '0',
          annualLimit: it.annualLimit !== undefined ? String(it.annualLimit) : '',
          lifetimeLimit: it.lifetimeLimit !== undefined ? String(it.lifetimeLimit) : '',
          visitLimit: it.visitLimit !== undefined ? String(it.visitLimit) : '',
          waitingPeriodMonths: it.waitingPeriodMonths !== undefined ? String(it.waitingPeriodMonths) : '',
          cashBenefitPerUnit: it.cashBenefitPerUnit !== undefined ? String(it.cashBenefitPerUnit) : '',
          cashBenefitUnit: it.cashBenefitUnit ?? '',
          cashBenefitMaxUnits: it.cashBenefitMaxUnits !== undefined ? String(it.cashBenefitMaxUnits) : '',
          sortOrder: ii,
        })),
      }))
    );
    toast.success('Form populated from PDF');
  };

  const handleSave = async () => {
    if (!currentOrg || !state.name.trim() || !state.providerId || !state.insuranceTypeId) {
      toast.error('Please fill in all required fields');
      return;
    }
    setIsSubmitting(true);
    try {
      const planArgs = {
        name: state.name.trim(),
        providerId: state.providerId as Id<'insuranceProviders'>, // AS_JUSTIFIED: one-time Id cast from controlled select value
        insuranceTypeId: state.insuranceTypeId as Id<'orgInsuranceTypes'>, // AS_JUSTIFIED: one-time Id cast from controlled select value
        currency: state.currency.trim(),
        maxAnnualBenefit: parseFloat(state.maxAnnualBenefit) || 0,
        annualDeductible: parseFloat(state.annualDeductible) || 0,
        coinsurancePlanPays: parseFloat(state.coinsurancePlanPays) || 100,
        coinsuranceYouPay: parseFloat(state.coinsuranceYouPay) || 0,
        areaOfCoverage: state.areaOfCoverage.trim() || undefined,
        description: state.description.trim() || undefined,
      };
      let savedPlanId: Id<'benefitPlans'>;
      if (isEditing && planId) {
        await updatePlan({ planId, ...planArgs });
        savedPlanId = planId;
      } else {
        savedPlanId = await createPlan({ orgId: currentOrg._id, ...planArgs });
      }
      for (const sec of sectionForm.sections) {
        const sectionId = await createSection({
          planId: savedPlanId, orgId: currentOrg._id,
          name: sec.name.trim() || 'Untitled Section', sortOrder: sec.sortOrder,
        });
        for (const item of sec.items) {
          await createItem({
            sectionId, planId: savedPlanId, orgId: currentOrg._id,
            name: item.name.trim() || 'Untitled Item',
            description: item.description.trim() || undefined,
            planPaysPercent: parseFloat(item.planPaysPercent) || 100,
            youPayPercent: parseFloat(item.youPayPercent) || 0,
            annualLimit: item.annualLimit ? parseFloat(item.annualLimit) : undefined,
            lifetimeLimit: item.lifetimeLimit ? parseFloat(item.lifetimeLimit) : undefined,
            visitLimit: item.visitLimit ? parseFloat(item.visitLimit) : undefined,
            waitingPeriodMonths: item.waitingPeriodMonths ? parseFloat(item.waitingPeriodMonths) : undefined,
            cashBenefitPerUnit: item.cashBenefitPerUnit ? parseFloat(item.cashBenefitPerUnit) : undefined,
            cashBenefitUnit: item.cashBenefitUnit.trim() || undefined,
            cashBenefitMaxUnits: item.cashBenefitMaxUnits ? parseFloat(item.cashBenefitMaxUnits) : undefined,
            sortOrder: item.sortOrder,
          });
        }
      }
      toast.success(isEditing ? 'Plan updated' : 'Plan created');
      navigate(`/insurance/benefit-plans/${savedPlanId}`);
    } catch (err) {
      toast.error('Failed to save plan');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/insurance/benefit-plans')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">{isEditing ? 'Edit Benefit Plan' : 'New Benefit Plan'}</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" /> Import from PDF
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save Plan
          </Button>
        </div>
      </div>

      <PlanDetailsCard state={state} providers={providers} orgTypes={orgTypes} setField={setField} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Benefit Sections</h2>
          <Button variant="outline" size="sm" onClick={sectionForm.addSection}>
            <Plus className="mr-1 h-4 w-4" /> Add Section
          </Button>
        </div>
        {sectionForm.sections.length === 0 && (
          <p className={cn('text-sm text-muted-foreground py-6 text-center border rounded-lg border-dashed')}>
            No sections yet. Add a section to define benefit categories.
          </p>
        )}
        {sectionForm.sections.map((sec, idx) => (
          <SectionEditor key={sec.tempId} section={sec}
            isFirst={idx === 0} isLast={idx === sectionForm.sections.length - 1}
            onUpdateName={n => sectionForm.updateSection(sec.tempId, n)}
            onRemove={() => sectionForm.removeSection(sec.tempId)}
            onMoveUp={() => sectionForm.moveSectionUp(sec.tempId)}
            onMoveDown={() => sectionForm.moveSectionDown(sec.tempId)}
            onAddItem={() => sectionForm.addItem(sec.tempId)}
            onUpdateItem={(itemTempId, patch) => sectionForm.updateItem(sec.tempId, itemTempId, patch)}
            onRemoveItem={itemTempId => sectionForm.removeItem(sec.tempId, itemTempId)}
            onMoveItemUp={itemTempId => sectionForm.moveItemUp(sec.tempId, itemTempId)}
            onMoveItemDown={itemTempId => sectionForm.moveItemDown(sec.tempId, itemTempId)}
          />
        ))}
      </div>

      <ImportPlanDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={handleImport} />
    </div>
  );
}
